const RECENT_SEARCHES_KEY = "unbound-recent-searches";
const RECENT_SEARCHES_LIMIT = 6;
const PREDICTIVE_CLICK_NAVIGATION_TIMEOUT_MS = 1200;

function normalizeSearchTerm(value) {
  return (value || "").trim().replace(/\s+/g, " ");
}

function getRecentSearches() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_SEARCHES_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function saveRecentSearch(term) {
  const normalized = normalizeSearchTerm(term);
  if (!normalized) return;

  const recent = getRecentSearches().filter(
    (item) => item.toLowerCase() !== normalized.toLowerCase(),
  );

  recent.unshift(normalized);
  window.localStorage.setItem(
    RECENT_SEARCHES_KEY,
    JSON.stringify(recent.slice(0, RECENT_SEARCHES_LIMIT)),
  );
}

function clearRecentSearches() {
  window.localStorage.removeItem(RECENT_SEARCHES_KEY);
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (character) => {
    const entities = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };

    return entities[character];
  });
}

class HeaderSearch extends HTMLElement {
  constructor() {
    super();

    this.prominentDesktopTrigger = this.querySelector('[data-search-trigger="prominent-desktop"]');
    this.drawer = this.querySelector(".search-drawer");
    this.overlay = this.querySelector("[data-search-close]");
    this.closeButtons = this.querySelectorAll("[data-search-close]");
    this.form = this.querySelector("[data-search-form]");
    this.input = this.querySelector("[data-search-input]");
    this.clearButton = this.querySelector("[data-search-clear]");
    this.defaultState = this.querySelector("[data-default-state]");
    this.typingState = this.querySelector("[data-typing-state]");
    this.suggestionsContainer = this.querySelector("[data-search-suggestions]");
    this.recentSection = this.querySelector("[data-recent-section]");
    this.recentContainer = this.querySelector("[data-recent-searches]");
    this.clearRecentButton = this.querySelector("[data-clear-recent]");

    this.prominentMobileTrigger = document.querySelector('[data-search-trigger="prominent-mobile"]');
    this.headerWrapper = this.closest(".section-header")?.querySelector(".header-wrapper") || null;

    this.searchUrl = window.routes?.search_url || "/search";
    this.searchApi = window.UnboundSearchApi || null;

    this.abortController = null;
    this.activeSuggestionState = this.getEmptySuggestionState();
    this.latestPredictiveResponse = null;
    this.handleInput = this.debounce(() => this.updateSuggestionState(), 200);
    this.handleKeyup = this.handleKeyup.bind(this);
    this.handlePageHide = this.handlePageHide.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleHeaderWrapperClick = this.handleHeaderWrapperClick.bind(this);
    this.handleDrawerClick = this.handleDrawerClick.bind(this);
    this.restoreWindowOnScroll = null;
  }

  connectedCallback() {
    this.mountDrawer();
    this.setTriggerAccessibility(this.prominentDesktopTrigger, "Open search");
    this.setTriggerAccessibility(this.prominentMobileTrigger, "Open search");

    this.prominentDesktopTrigger?.addEventListener("click", (event) => {
      event.preventDefault();
      this.toggleDrawer(this.prominentDesktopTrigger);
    });

    this.prominentMobileTrigger?.addEventListener("click", (event) => {
      event.preventDefault();
      this.openDrawer(this.prominentMobileTrigger);
    });

    this.closeButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.closeDrawer();
      });
    });

    this.clearButton?.addEventListener("click", () => {
      if (!this.input) return;
      this.input.value = "";
      this.updateSuggestionState({ immediate: true });
      this.input.focus();
    });

    this.clearRecentButton?.addEventListener("click", () => {
      clearRecentSearches();
      this.renderRecentSearches();
    });

    this.input?.addEventListener("input", () => this.handleInput());

    this.form?.addEventListener("submit", (event) => {
      const query = normalizeSearchTerm(this.input?.value);

      if (!query) {
        event.preventDefault();
        this.updateSuggestionState({ immediate: true });
        return;
      }

      saveRecentSearch(query);
      this.activeSuggestionState.query = query;
      this.activeSuggestionState.submitted = true;
      this.activeSuggestionState.exited = false;

      const redirectUrl = this.getPredictiveRedirectUrl(query);
      if (redirectUrl) {
        event.preventDefault();
        window.location.assign(redirectUrl);
        return;
      }

      this.getTracker()?.trackSearchSubmitted?.(
        {
          query,
          surface: "RESULTS",
        },
        { transport: "beacon" },
      );
    });

    this.drawer?.addEventListener("click", this.handleDrawerClick);

    document.addEventListener("keydown", this.handleKeyup);
    window.addEventListener("pagehide", this.handlePageHide);
    window.addEventListener("resize", this.handleResize);
    this.headerWrapper?.addEventListener("click", this.handleHeaderWrapperClick, true);
    this.renderRecentSearches();
    this.updateSuggestionState({ immediate: true });
    this.updateProminentMobileOffset();
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleKeyup);
    window.removeEventListener("pagehide", this.handlePageHide);
    window.removeEventListener("resize", this.handleResize);
    this.headerWrapper?.removeEventListener("click", this.handleHeaderWrapperClick, true);
    this.drawer?.removeEventListener("click", this.handleDrawerClick);
    this.abortController?.abort();
  }

  mountDrawer() {
    if (!this.drawer || !document.body || this.drawer.parentElement === document.body) {
      return;
    }

    document.body.appendChild(this.drawer);
  }

  async handleDrawerClick(event) {
    const searchLink = event.target.closest("[data-search-query]");
    if (!searchLink) return;

    const query = searchLink.getAttribute("data-search-query");
    saveRecentSearch(query);

    const shouldInterceptNavigation =
      this.isPredictiveSuggestionLink(searchLink) && this.isPlainPrimaryNavigationClick(event, searchLink);

    event.__unboundSearchTrackingHandled = true;

    if (shouldInterceptNavigation) {
      event.preventDefault();
    }

    const trackingPromise = Promise.resolve(
      this.trackSuggestionClick(
        searchLink,
        shouldInterceptNavigation
          ? { transport: "fetch", preferNativeTransport: true }
          : { transport: "beacon" },
      ),
    );

    if (!shouldInterceptNavigation) {
      return;
    }

    const href = searchLink.href;
    if (!href) return;

    await Promise.race([trackingPromise, this.wait(PREDICTIVE_CLICK_NAVIGATION_TIMEOUT_MS)]);
    window.location.assign(href);
  }

  getTracker() {
    return window.UnboundSearchTracking || null;
  }

  getEmptySuggestionState() {
    return {
      query: "",
      resultCount: 0,
      clicked: false,
      submitted: false,
      exited: false,
      impressionKey: "",
    };
  }

  updateActiveSuggestionState(query, resultCount) {
    const normalizedQuery = normalizeSearchTerm(query);
    const nextKey = normalizedQuery ? `${normalizedQuery}::${resultCount}` : "";

    if (!nextKey) {
      return false;
    }

    if (this.activeSuggestionState.impressionKey === nextKey) {
      this.activeSuggestionState.query = normalizedQuery;
      this.activeSuggestionState.resultCount = resultCount;
      return false;
    }

    this.activeSuggestionState = {
      query: normalizedQuery,
      resultCount,
      clicked: false,
      submitted: false,
      exited: false,
      impressionKey: nextKey,
    };

    return true;
  }

  trackSuggestionImpression(query, resultCount) {
    const isNewState = this.updateActiveSuggestionState(query, resultCount);
    if (!isNewState) return;

    const tracker = this.getTracker();
    if (!tracker?.isEnabled?.()) return;

    if (resultCount === 0) {
      tracker.trackZeroResults({
        query,
        surface: "PREDICTIVE",
        resultCount: 0,
      });
      return;
    }

    tracker.trackPredictiveImpression({
      query,
      resultCount,
    });
  }

  trackSuggestionClick(searchLink, transportOptions = { transport: "beacon" }) {
    const tracker = this.getTracker();
    const matchedQuery =
      normalizeSearchTerm(searchLink.dataset.searchMatchedQuery) ||
      normalizeSearchTerm(this.input?.value) ||
      normalizeSearchTerm(searchLink.dataset.searchQuery);
    const isPredictiveSuggestion = this.isPredictiveSuggestionLink(searchLink);

    if (this.activeSuggestionState.query === matchedQuery) {
      this.activeSuggestionState.clicked = true;
      this.activeSuggestionState.exited = false;
    }

    if (!tracker?.isEnabled?.() || !matchedQuery) return;

    const fallbackPosition = Array.from(
      searchLink.parentElement?.querySelectorAll("[data-search-query]") || [],
    ).indexOf(searchLink) + 1;
    const position = Number.parseInt(searchLink.dataset.searchPosition || fallbackPosition, 10);
    const resultCount = Number.parseInt(
      searchLink.dataset.searchResultCount || this.activeSuggestionState.resultCount || 0,
      10,
    );
    const itemType = searchLink.dataset.searchItemType || "query";
    const productId = searchLink.dataset.searchProductId || "";
    const suggestionLabel =
      itemType === "product"
        ? ""
        : normalizeSearchTerm(searchLink.dataset.searchSuggestionLabel || searchLink.dataset.searchQuery);
    const trackingContext = this.getTrackingContextPayload();
    const trackingPromises = [];

    if (isPredictiveSuggestion) {
      trackingPromises.push(
        tracker.trackPredictiveClick(
          {
            query: matchedQuery,
            productId,
            suggestionLabel,
            position,
            resultCount,
            ...trackingContext,
          },
          transportOptions,
        ),
      );
    }

    if (itemType === "query") {
      trackingPromises.push(
        tracker.trackSearchSubmitted?.(
          {
            query: matchedQuery,
            surface: "RESULTS",
            ...trackingContext,
          },
          transportOptions,
        ),
      );
    }

    if (!trackingPromises.length) {
      return Promise.resolve(false);
    }

    return Promise.allSettled(trackingPromises).then((results) =>
      results.some((result) => result.status === "fulfilled" && Boolean(result.value)),
    );
  }

  getTrackingContextPayload() {
    const pageContext = this.searchApi?.getPageContext?.() || {};

    return {
      marketId: pageContext.marketId || "",
      context: {
        type: pageContext.contextType || "",
        handle: pageContext.contextHandle || "",
        gender: pageContext.contextGender || "",
      },
    };
  }

  isPredictiveSuggestionLink(searchLink) {
    if (!(searchLink instanceof Element)) return false;

    return (
      Boolean(searchLink.closest("[data-search-suggestions]")) ||
      Boolean(searchLink.closest("[data-typing-state]")) ||
      Boolean(searchLink.dataset.searchPosition) ||
      Boolean(searchLink.dataset.searchProductId)
    );
  }

  isPlainPrimaryNavigationClick(event, searchLink) {
    if (!event || !(searchLink instanceof HTMLAnchorElement)) return false;
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;

    const target = (searchLink.getAttribute("target") || "").toLowerCase();
    return !target || target === "_self";
  }

  wait(durationMs) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, durationMs);
    });
  }

  trackSuggestionExit() {
    const tracker = this.getTracker();
    const { query, resultCount, clicked, submitted, exited } = this.activeSuggestionState;

    if (!query || clicked || submitted || exited) return;

    this.activeSuggestionState.exited = true;

    if (!tracker?.isEnabled?.()) return;

    tracker.trackExit(
      {
        query,
        surface: "PREDICTIVE",
        resultCount,
      },
      { transport: "beacon" },
    );
  }

  handlePageHide() {
    if (!this.drawer?.classList.contains("active")) return;
    this.trackSuggestionExit();
  }

  setTriggerAccessibility(element, label) {
    if (!element) return;

    if (element.tagName !== "BUTTON") {
      element.setAttribute("role", "button");
      element.setAttribute("tabindex", "0");
      element.addEventListener("keydown", (event) => {
        if (event.code === "Space" || event.code === "Enter") {
          event.preventDefault();
          element.click();
        }
      });
    }

    element.setAttribute("aria-label", label);
  }

  handleKeyup(event) {
    if (event.key === "Escape" && this.drawer?.classList.contains("active")) {
      this.closeDrawer();
    }
  }

  handleResize() {
    this.updateProminentMobileOffset();
  }

  handleHeaderWrapperClick(event) {
    if (!this.drawer?.classList.contains("active")) return;
    if (!window.matchMedia("(max-width: 989.98px)").matches) return;
    if (!this.headerWrapper?.contains(event.target)) return;
    if (event.target.closest(".search-drawer")) return;

    event.preventDefault();
    event.stopPropagation();
    this.closeDrawer();
  }

  toggleDrawer(triggeredBy) {
    if (this.drawer?.classList.contains("active")) {
      this.closeDrawer();
      return;
    }

    this.openDrawer(triggeredBy);
  }

  openDrawer(triggeredBy) {
    if (!this.drawer) return;

    this.activeElement = triggeredBy || document.activeElement;
    this.revealStickyHeaderForSearch();
    this.drawer.classList.add("active");
    document.body.classList.add("search-opened");
    document.documentElement.classList.add("search-opened");
    if (window.matchMedia("(max-width: 989.98px)").matches) {
      document.body.classList.add("search-prominence-mobile-open");
      document.documentElement.classList.add("search-prominence-mobile-open");
    }
    if (window.matchMedia("(min-width: 990px)").matches) {
      document.body.classList.add("search-prominence-desktop-open");
      document.documentElement.classList.add("search-prominence-desktop-open");
    }
    this.setExpandedState(true);
    this.updateProminentMobileOffset();
    document.dispatchEvent(new CustomEvent("search:opened"));

    window.requestAnimationFrame(() => {
      this.updateProminentMobileOffset();
    });

    this.renderRecentSearches();
    this.updateSuggestionState({ immediate: true });

    this.drawer.addEventListener(
      "transitionend",
      () => {
        trapFocus(this.drawer.querySelector(".search-drawer__inner"), this.input || this.closeButtons[0]);
      },
      { once: true },
    );

    this.input?.focus({ preventScroll: true });
  }

  openOverlay(triggeredBy) {
    this.openDrawer(triggeredBy);
  }

  closeDrawer() {
    if (!this.drawer) return;

    this.trackSuggestionExit();
    this.drawer.classList.remove("active");
    document.body.classList.remove("search-opened");
    document.documentElement.classList.remove("search-opened");
    document.body.classList.remove("search-prominence-mobile-open");
    document.documentElement.classList.remove("search-prominence-mobile-open");
    document.body.classList.remove("search-prominence-desktop-open");
    document.documentElement.classList.remove("search-prominence-desktop-open");
    this.resetStickyHeaderAfterSearch();
    this.setExpandedState(false);
    removeTrapFocus(this.activeElement);
    this.activeSuggestionState = this.getEmptySuggestionState();
    this.updateProminentMobileOffset();
  }

  closeOverlay() {
    this.closeDrawer();
  }

  async updateSuggestionState({ immediate = false } = {}) {
    const query = normalizeSearchTerm(this.input?.value);

    this.clearButton?.classList.toggle("hidden", !query);

    if (!query) {
      this.abortController?.abort();
      this.latestPredictiveResponse = null;
      this.typingState?.classList.add("hidden");
      this.defaultState?.classList.remove("hidden");
      this.renderRecentSearches();
      return;
    }

    this.defaultState?.classList.add("hidden");
    this.typingState?.classList.remove("hidden");
    this.renderRecentSearches();

    if (immediate) {
      await this.renderSuggestions(query);
      return;
    }

    await this.renderSuggestions(query);
  }

  async renderSuggestions(query) {
    if (!this.suggestionsContainer) return;

    const suggestions = await this.fetchSuggestions(query);
    const normalizedQuery = normalizeSearchTerm(query);

    if (normalizedQuery !== normalizeSearchTerm(this.input?.value)) {
      return;
    }

    const actualResultCount = suggestions.resultCount;
    const redirectUrl = this.getRedirectUrl(suggestions.redirect);

    const items = [
      {
        label: normalizedQuery,
        href: redirectUrl || this.buildSearchHref(normalizedQuery),
        matchedTerm: normalizedQuery,
        itemType: "query",
        suggestionLabel: normalizedQuery,
      },
      ...suggestions.suggestions.map((item) => ({
        label: item.label,
        href: item.targetUrl || this.buildSearchHref(item.targetQuery || item.query || item.label),
        matchedTerm: normalizedQuery,
        itemType: item.itemType || "query",
        suggestionLabel: item.label,
      })),
      ...suggestions.products.map((product) => ({
        label: product.title,
        href: product.url || this.buildSearchHref(product.title),
        matchedTerm: normalizedQuery,
        itemType: "product",
        productId: product.productId,
      })),
    ].filter((item) => item?.label);
    const uniqueItems = [];
    const seen = new Set();

    items.forEach((item) => {
      const normalizedItem = normalizeSearchTerm(item.label);
      const key = normalizedItem.toLowerCase();

      if (!normalizedItem || seen.has(key)) return;

      seen.add(key);
      uniqueItems.push({
        ...item,
        label: normalizedItem,
      });
    });

    this.suggestionsContainer.innerHTML = uniqueItems
      .map((item, index) =>
        this.buildSuggestionLink({
          query: item.label,
          matchedTerm: item.matchedTerm,
          href: item.href,
          itemType: item.itemType,
          productId: item.productId,
          suggestionLabel: item.suggestionLabel,
          position: index + 1,
          resultCount: actualResultCount,
        }),
      )
      .join("");

    this.trackSuggestionImpression(normalizedQuery, actualResultCount);
  }

  async fetchSuggestions(query) {
    const normalizedQuery = normalizeSearchTerm(query);
    if (!normalizedQuery) {
      this.latestPredictiveResponse = null;
      return {
        suggestions: [],
        products: [],
        redirect: null,
        resultCount: 0,
      };
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const response = await this.searchApi?.fetchPredictive?.(normalizedQuery, {
        signal: this.abortController.signal,
      });
      const normalizedSuggestions = Array.isArray(response?.suggestions)
        ? response.suggestions.filter((item) => item?.label).slice(0, 8)
        : [];
      const normalizedProducts = Array.isArray(response?.products)
        ? response.products
            .map((product) => ({
              ...product,
              productId:
                product.productId || this.searchApi?.normalizeProductId?.(product.id) || this.getProductTrackingId(product),
            }))
            .filter((product) => product?.title)
            .slice(0, 8)
        : [];

      this.latestPredictiveResponse = response || null;

      return {
        suggestions: normalizedSuggestions,
        products: normalizedProducts,
        redirect: response?.redirect || null,
        resultCount:
          typeof response?.resultCount === "number"
            ? response.resultCount
            : normalizedSuggestions.length + normalizedProducts.length,
      };
    } catch (error) {
      if (error.name === "AbortError") {
        return {
          suggestions: [],
          products: [],
          redirect: null,
          resultCount: 0,
        };
      }

      this.latestPredictiveResponse = null;
      return {
        suggestions: [],
        products: [],
        redirect: null,
        resultCount: 0,
      };
    }
  }

  getRedirectUrl(redirect) {
    return this.searchApi?.getRedirectUrl?.(redirect) || "";
  }

  getPredictiveRedirectUrl(query) {
    const normalizedQuery = normalizeSearchTerm(query);
    if (!normalizedQuery) return "";

    if (
      normalizeSearchTerm(this.latestPredictiveResponse?.normalizedQuery || this.latestPredictiveResponse?.query) !==
      normalizedQuery
    ) {
      return "";
    }

    return this.getRedirectUrl(this.latestPredictiveResponse?.redirect);
  }

  getProductTrackingId(product) {
    const rawProductId = String(product?.admin_graphql_api_id || product?.id || "").trim();
    if (!rawProductId) return "";
    if (rawProductId.startsWith("gid://")) return rawProductId;

    const numericProductId = rawProductId.match(/\d+/)?.[0];
    return numericProductId ? `gid://shopify/Product/${numericProductId}` : "";
  }

  renderRecentSearches() {
    if (!this.recentSection || !this.recentContainer) return;

    const recentSearches = getRecentSearches();

    if (!recentSearches.length) {
      this.recentSection.classList.add("hidden");
      this.recentContainer.innerHTML = "";
      return;
    }

    this.recentSection.classList.remove("hidden");
    this.recentContainer.innerHTML = recentSearches
      .map((term) =>
        this.buildSuggestionLink({
          query: term,
          suggestionLabel: term,
        }),
      )
      .join("");
  }

  buildSearchHref(query) {
    const normalizedQuery = normalizeSearchTerm(query);
    return `${this.searchUrl}?q=${encodeURIComponent(normalizedQuery)}&type=product&options%5Bunavailable_products%5D=hide`;
  }

  buildSuggestionLink({
    query,
    matchedTerm = "",
    href = "",
    itemType = "query",
    productId = "",
    suggestionLabel = "",
    position = "",
    resultCount = "",
  }) {
    const normalizedQuery = normalizeSearchTerm(query);
    const resolvedHref = href || this.buildSearchHref(normalizedQuery);
    const label = this.buildSuggestionLabel(normalizedQuery, matchedTerm);

    return `
      <a
        class="search-drawer__link"
        href="${escapeHtml(resolvedHref)}"
        data-search-query="${escapeHtml(normalizedQuery)}"
        data-search-item-type="${escapeHtml(itemType)}"
        data-search-product-id="${escapeHtml(productId)}"
        data-search-suggestion-label="${escapeHtml(suggestionLabel || normalizedQuery)}"
        data-search-position="${escapeHtml(position)}"
        data-search-result-count="${escapeHtml(resultCount)}"
        data-search-matched-query="${escapeHtml(matchedTerm || normalizedQuery)}"
      >
        ${label}
      </a>
    `;
  }

  buildSuggestionLabel(label, matchedTerm = "") {
    const normalizedLabel = String(label || "");
    const normalizedMatchedTerm = normalizeSearchTerm(matchedTerm);

    if (!normalizedMatchedTerm) {
      return escapeHtml(normalizedLabel);
    }

    const labelLower = normalizedLabel.toLowerCase();
    const matchedLower = normalizedMatchedTerm.toLowerCase();
    const matchIndex = labelLower.indexOf(matchedLower);

    if (matchIndex === -1) {
      return escapeHtml(normalizedLabel);
    }

    const before = normalizedLabel.slice(0, matchIndex);
    const match = normalizedLabel.slice(matchIndex, matchIndex + normalizedMatchedTerm.length);
    const after = normalizedLabel.slice(matchIndex + normalizedMatchedTerm.length);
    let html = "";

    if (before) {
      html += `<span class="search-drawer__link-rest">${escapeHtml(before)}</span>`;
    }

    html += `<span class="search-drawer__link-match">${escapeHtml(match)}</span>`;

    if (after) {
      html += `<span class="search-drawer__link-rest">${escapeHtml(after)}</span>`;
    }

    return html;
  }

  debounce(callback, wait) {
    let timer = null;

    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback.apply(this, args), wait);
    };
  }

  setExpandedState(isExpanded) {
    const expandedValue = isExpanded ? "true" : "false";

    [
      this.prominentDesktopTrigger,
      this.prominentMobileTrigger,
    ].forEach((trigger) => {
      trigger?.setAttribute("aria-expanded", expandedValue);
    });
  }

  revealStickyHeaderForSearch() {
    if (!window.matchMedia("(max-width: 989.98px)").matches) return;

    const stickyHeader = document.querySelector(".mega-sticky-header");
    if (!stickyHeader) return;

    stickyHeader?.classList.add("search-force-visible");
    stickyHeader?.classList.remove("hide");
    stickyHeader.style.transform = "translateY(0)";
    stickyHeader.style.visibility = "visible";
    stickyHeader.style.pointerEvents = "auto";

    if (this.restoreWindowOnScroll === null) {
      this.restoreWindowOnScroll = window.onscroll;
    }

    window.onscroll = (event) => {
      stickyHeader.classList.remove("hide");
      stickyHeader.style.transform = "translateY(0)";

      if (typeof this.restoreWindowOnScroll === "function" && !document.body.classList.contains("search-opened")) {
        return this.restoreWindowOnScroll.call(window, event);
      }

      return undefined;
    };
  }

  resetStickyHeaderAfterSearch() {
    const stickyHeader = document.querySelector(".mega-sticky-header");
    if (stickyHeader) {
      stickyHeader.classList.remove("search-force-visible");
      stickyHeader.style.removeProperty("transform");
      stickyHeader.style.removeProperty("visibility");
      stickyHeader.style.removeProperty("pointer-events");
    }

    if (this.restoreWindowOnScroll !== null) {
      window.onscroll = this.restoreWindowOnScroll;
      this.restoreWindowOnScroll = null;
    }
  }

  updateProminentMobileOffset() {
    const stickyHeader = document.querySelector(".mega-sticky-header");
    const mobileSearchRow = document.querySelector("[data-search-prominence-mobile-row]");
    const announcementBar =
      document.querySelector("#shopify-section-announcement-bar") ||
      document.querySelector(".announcement-bar");
    const isMobileViewport = window.matchMedia("(max-width: 989.98px)").matches;
    const isDrawerOpen = this.drawer?.classList.contains("active");
    const mobileSearchRowHeight =
      isMobileViewport && isDrawerOpen ? mobileSearchRow?.getBoundingClientRect().height || 0 : 0;
    const stickyHeaderHeight = Math.max(
      (stickyHeader?.getBoundingClientRect().height || 0) - mobileSearchRowHeight,
      0,
    );
    const announcementBarHeight = announcementBar?.getBoundingClientRect().height || 0;
    const desktopTriggerRect = this.prominentDesktopTrigger?.getBoundingClientRect();
    const desktopPanelRight = desktopTriggerRect
      ? Math.max(Math.round(window.innerWidth - desktopTriggerRect.right), 20)
      : 20;

    document.documentElement.style.setProperty(
      "--search-prominence-mobile-header-height",
      `${Math.round(stickyHeaderHeight)}px`,
    );
    document.documentElement.style.setProperty(
      "--search-prominence-desktop-panel-right",
      `${desktopPanelRight}px`,
    );
    document.documentElement.style.setProperty(
      "--search-prominence-desktop-top-offset",
      `${Math.round(announcementBarHeight)}px`,
    );
  }

  getDesktopStickyNavBottom(stickyHeaderBottom = 0) {
    if (!window.matchMedia("(min-width: 750px)").matches) {
      return 0;
    }

    const stickySelectors = [
      ".collection--filtering .button-filters.hide-desktop.button-filters--A",
      ".collection--filtering .facets-vertical-sort.small-hide.no-js-hidden",
    ];

    let maxBottom = 0;

    stickySelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.height <= 0 || rect.bottom <= 0) return;
        if (rect.top > stickyHeaderBottom + 24) return;
        maxBottom = Math.max(maxBottom, rect.bottom);
      });
    });

    return maxBottom;
  }
}

customElements.define("header-search", HeaderSearch);
